import type { ReactNode } from 'react';

import { JobGlyph } from '@/components/JobGlyph/JobGlyph';
import { JobStatusBadge } from '@/components/JobStatusBadge/JobStatusBadge';
import { TrashButton } from '@/components/TrashButton/TrashButton';
import { formatRelative } from '@/lib/format';
import { isAudioJob, isInFlight, jobTitle, type Job } from '@/papermill/api';
import { jobState, sourceOf } from '@/papermill/stages';
import { formatDuration } from '@/papermill/transcript';

import styles from './JobList.module.css';

export interface JobListProps {
  jobs: Job[];
  /** Wraps a row in a link to the job; routing is the caller's. */
  renderLink: (job: Job, children: ReactNode) => ReactNode;
  /** Id of a job to draw attention to, e.g. the one just submitted. */
  highlightId?: string | null;
  emptyAction?: ReactNode;
  /** Shows a trash button on each card. */
  onDelete?: (job: Job) => void;
}

function duration(job: Job): string | null {
  if (!job.submittedAt || !job.completedAt) {
    return null;
  }
  const seconds = Math.max(0, (Date.parse(job.completedAt) - Date.parse(job.submittedAt)) / 1000);
  return seconds < 60
    ? `${Math.round(seconds)}s`
    : `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
}

/** Every playground job, newest first: what it was made from and what went wrong, at a glance. */
export function JobList({
  jobs,
  renderLink,
  highlightId = null,
  emptyAction,
  onDelete,
}: JobListProps) {
  if (jobs.length === 0) {
    return (
      <div className={styles.empty}>
        <span className={styles.emptyIcon} aria-hidden="true">
          ✦
        </span>
        <p className={styles.emptyTitle}>No jobs yet</p>
        <p className={styles.muted}>
          Upload a document, snap a photo or send a recording, and watch it turn into a graph of
          people, companies, accounts and addresses.
        </p>
        {emptyAction}
      </div>
    );
  }

  return (
    <ul className={styles.list} aria-label="Jobs">
      {jobs.map((job) => {
        const state = jobState(job);
        const took = duration(job);
        return (
          <li
            key={job.id}
            className={[
              styles.item,
              job.id === highlightId ? styles.highlight : '',
              onDelete ? styles.deletable : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {renderLink(
              job,
              <div className={styles.row}>
                <span className={`${styles.icon} ${styles[state.tone]}`}>
                  <JobGlyph
                    source={sourceOf(job)}
                    sample={Boolean(job.isSample)}
                    active={isInFlight(job)}
                  />
                </span>
                <span className={styles.main}>
                  <span className={styles.name}>
                    <span className={styles.filename}>{jobTitle(job)}</span>
                    {job.isSample ? <span className={styles.chip}>Sample</span> : null}
                  </span>
                  <span className={styles.meta}>
                    {job.submittedAt ? formatRelative(job.submittedAt) : null}
                    {isAudioJob(job)
                      ? job.durationMs
                        ? ` · ${formatDuration(job.durationMs)}`
                        : null
                      : job.pageCount
                        ? ` · ${job.pageCount} ${job.pageCount === 1 ? 'page' : 'pages'}`
                        : null}
                    {job.credits !== null
                      ? ` · ${job.credits} ${job.credits === 1 ? 'credit' : 'credits'}`
                      : null}
                    {took ? ` · ${took}` : null}
                  </span>
                  {state.detail ? (
                    <span className={`${styles.detail} ${styles[state.tone]}`}>{state.detail}</span>
                  ) : null}
                </span>
                <JobStatusBadge label={state.label} tone={state.tone} />
                <span className={styles.chevron} aria-hidden="true">
                  ›
                </span>
              </div>,
            )}
            {onDelete ? (
              <TrashButton
                className={styles.delete}
                label={`Delete ${jobTitle(job)}`}
                onClick={() => onDelete(job)}
              />
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
